/****************************************************
 * Module Name:  kapanHistoryModal.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState } from "react";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import { Avatar, Box, Button, Chip, Container, Tooltip } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import { useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Constants from "../../../utility/constants";
import "../../../style/modalstyle.css";
import logo from '../../../images/pansuriya.jpg'
import Utility from "../../../utility/utility";
import KapanCreationProxy from "../../../utility/proxy/manager/manufacturingProxy/kapanCreationProxy";
import renderCellExpand from "../../common/dataGridCellExpand";
import CommonSnackbar from "../../common/commonSnackBar";

const KapanHistoryModal = ({ kapanId, close, show }) => {
    const [data, setData] = useState([]);
    const [detailLoader, setDetailLoader] = useState(false);
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [columnNames, setColumnNames] = useState([]);

    useEffect(() => {
        handleSearch(kapanId);
    }, []);

    const handleSearch = (kapanId) => {
        setDetailLoader(true);
        KapanCreationProxy.GetAllKapanAuditByKapanId(
            kapanId,
            (response) => {
                if (response) {
                    let prevRow = null;
                    response.map((iRow) => {
                        if (prevRow === null) {
                            prevRow = iRow
                            // Considering the 1st row as the previous row, we wont be showing any color change in this
                            iRow.prevRow = iRow;
                        } else {
                            iRow.prevRow = prevRow;
                            prevRow = iRow;
                        }
                        return true;
                    });
                    const uniqueFilterValues = {
                        user: Utility.getDistinctOptions(response, "username"),
                        updatedbyuser: Utility.getDistinctOptions(response, "updatedbyusername"),
                        department: Utility.getDistinctOptions(response, "department_name"),
                        operation: Utility.getDistinctOptions(response, "operation"),
                        process: Utility.getDistinctOptions(response, "processname"),
                    };
                    const combinedFilterValues = {
                        user: uniqueFilterValues.user.map(value => {
                            const matchingItem = response.find(item => item.username === value);
                            return matchingItem ? `${matchingItem.userid} | ${value}` : value;
                        }),
                        updatedbyuser: uniqueFilterValues.updatedbyuser.map(value => {
                            const matchingItem = response.find(item => item.updatedbyusername === value);
                            return matchingItem ? `${matchingItem.updatedbyid} | ${value}` : value;
                        }),
                        department: uniqueFilterValues.department.map(value => {
                            const matchingItem = response.find(item => item.department_name === value);
                            return matchingItem ? `${matchingItem.department_id} | ${value}` : value;
                        }),
                        operation: uniqueFilterValues.operation,
                        processname: uniqueFilterValues.process.map(value => {
                            const matchingItem = response.find(item => item.processname === value);
                            return matchingItem ? `${matchingItem.processid} | ${value}` : value;
                        }),
                    };
                    const filterMap = generateFilterMap(Array.isArray(response) ? response : [response]);
                    prepareColumns(filterMap, combinedFilterValues);
                    setData(response);
                    setDetailLoader(false)
                } else {
                    setErrorSnackbar(true)
                    setDetailLoader(false)
                }
            }
        );
    };



    const generateFilterMap = (responseData) => {
        const filterMap = {};

        // Process issued department head
        const department = responseData.map((dept) => ({
            id: dept.next_department_head_id,
            username: dept.next_department_head_username,
        }));

        filterMap.next_department_head_username = [
            ...new Set(
                department.map((group) => `${group.id} | ${group.username}`)
            ),
        ];

        // Process issued department 
        const departmentGroup = responseData.map((dept) => ({
            id: dept.next_department_id,
            username: dept.next_department_name,
        }));

        filterMap.next_department_name = [
            ...new Set(
                departmentGroup.map((group) => `${group.id} | ${group.username}`)
            ),
        ];

        return filterMap;
    };

    const prepareColumns = (filterMap, combinedFilterValues) => {
        let columnNames = [];
        const columnData = initialColumns(combinedFilterValues);

        columnData.forEach(iCol => {
            if (iCol.field === "next_department_head_username") {
                iCol.type = "singleSelect";
                iCol.valueOptions = filterMap ? filterMap.next_department_head_username : [];
                iCol.filterOperators = Utility.customFilters(filterMap, iCol.field, 'next_department_head_id', 'next_department_head_username');
            }
            if (iCol.field === "next_department_name") {
                iCol.type = "singleSelect";
                iCol.valueOptions = filterMap ? filterMap.next_department_name : [];
                iCol.filterOperators = Utility.customFilters(filterMap, iCol.field, 'next_department_id', 'next_department_name');
            }
            if (iCol.field === "processname") {
                iCol.type = "singleSelect";
                iCol.valueOptions = filterMap ? filterMap.processname : [];
                iCol.filterOperators = Utility.customFilters(filterMap, iCol.field, 'processid', 'processname');
            }
            columnNames.push(iCol);
        });

        setColumnNames(columnNames);
    }

    const genericCellValueWithColorClass = (currVal, prevVal) => {
        if (!currVal || (typeof currVal === "string" && currVal.trim() === ''))
            currVal = '';
        if (!prevVal || (typeof prevVal === "string" && prevVal.trim() === ''))
            prevVal = '';

        return currVal !== prevVal ? 'blueCell' : ""
    }

    const initialColumns = (combinedFilterValues) => [
        {
            field: "kapanid",
            headerName: "KAPANID",
            headerAlign: "center",
            align: "center",
            type: "number",
            width: 80,
            renderCell: renderCellExpand,
        },
        {
            field: "carats",
            headerName: "CARATS",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            type: "number",
            renderCell: renderCellExpand,
            cellClassName: (params) => genericCellValueWithColorClass(params.row.carats, params.row.prevRow.carats)
        },
        {
            field: "price",
            headerName: "PPC",
            headerAlign: "center",
            align: "right",
            width: 70,
            type: "number",
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price),
            cellClassName: (params) => genericCellValueWithColorClass(params.row.price, params.row.prevRow.price)
        },
        {
            field: "createdat",
            headerName: "CREATED AT",
            headerAlign: "center",
            align: "center",
            minWidth: 180,
            flex: 1,
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
            cellClassName: (params) => genericCellValueWithColorClass(params.row.createdat, params.row.prevRow.createdat)
        },
        {
            field: "userid",
            headerName: "USER",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            type: "singleSelect",
            valueOptions: combinedFilterValues ? combinedFilterValues.user : [],
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => params.row.userid + " | " + params.row.username,
            renderCell: renderCellExpand,
            cellClassName: (params) => genericCellValueWithColorClass(params.row.userid, params.row.prevRow.userid)
        },
        {
            field: "department_id",
            headerName: "DEPARTMENT",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            type: "singleSelect",
            valueOptions: combinedFilterValues ? combinedFilterValues.department : [],
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => params.row.department_id + " | " + params.row.department_name,
            renderCell: renderCellExpand,
            cellClassName: (params) => genericCellValueWithColorClass(params.row.department_id, params.row.prevRow.department_id)
        },
        {
            field: "next_department_head_username",
            headerName: "ISSUED DEPARTMENT HEAD",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => (
                params.row.next_department_head_id ?
                    <Box sx={{ minHeight: "auto", mt: 1 }}>
                        <Chip
                            avatar={<Avatar>{params.row.next_department_head_id}</Avatar>}
                            label={params.row.next_department_head_username}
                            sx={{ margin: "0px 10px 10px 0px" }}
                        />
                    </Box>
                    : '-'
            ),
            cellClassName: (params) => genericCellValueWithColorClass(params.row.next_department_head_id, params.row.prevRow.next_department_head_id),
            disableExport: true,

        },
        {
            field: "next_department_name",
            headerName: "ISSUED DEPARTMENT NAME",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => (
                params.row.next_department_id ?
                    <Box sx={{ minHeight: "auto", mt: 1 }}>
                        <Chip
                            avatar={<Avatar>{params.row.next_department_id}</Avatar>}
                            label={params.row.next_department_name}
                            sx={{ margin: "0px 10px 10px 0px" }}
                        />
                    </Box> : '-'
            ),
            cellClassName: (params) => genericCellValueWithColorClass(params.row.next_department_id, params.row.prevRow.next_department_id),
            disableExport: true,
        },
        {
            field: "processname",
            headerName: "PROCESS",
            headerAlign: "center",
            align: "center",
            width: 250,
            renderCell: (params) => {
                if (params.row.processid == null || params.row.processname == null)
                    return '-';
                return (
                    <Box sx={{ minHeight: "auto", mt: 1 }}>
                        <Chip
                            avatar={<Avatar>{params.row.processid}</Avatar>}
                            label={params.row.processname}
                            sx={{ margin: "0px 10px 10px 0px" }}
                        />
                    </Box>
                )
            },
            cellClassName: (params) => genericCellValueWithColorClass(params.row.processid, params.row.prevRow.processid),
            disableExport: true,
        },
        {
            field: "operation",
            headerName: "OPERATION",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            type: "singleSelect",
            valueOptions: combinedFilterValues ? combinedFilterValues.operation : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            cellClassName: (params) => genericCellValueWithColorClass(params.row.operation, params.row.prevRow.operation)
        },
        {
            field: "updatedbyid",
            headerName: "UPDATED BY",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            type: "singleSelect",
            valueOptions: combinedFilterValues ? combinedFilterValues.updatedbyuser : [],
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => params.row.updatedbyid + " | " + params.row.updatedbyusername,
            renderCell: renderCellExpand,
            cellClassName: (params) => genericCellValueWithColorClass(params.row.updatedbyid, params.row.prevRow.updatedbyid)
        },
    ];

    return (
        <div>
            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />
            <Box>
                <Modal show={show} onHide={close} dialogClassName="bhb" enforceFocus={false}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <img
                                src={logo}
                                height="40pt"
                                width="40pt"
                                alt={Constants.COMPANY_SITE}
                            />
                            &nbsp;&nbsp;Kapan History
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Container maxWidth="xxl">
                            <Box
                                sx={{
                                    backgroundColor: '#fff',
                                    color: '#000',
                                    width: "100%",
                                    position: "relative",
                                    "& .MuiTablePagination-selectLabel": {
                                        marginTop: "15px",
                                    },
                                    "& .MuiTablePagination-displayedRows": {
                                        marginTop: "15px",
                                    },
                                    "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                        fontWeight: Constants.defaultFontWeight,
                                    },
                                    "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                                        color: "#1976d2",
                                        padding: "4px 5px",
                                        borderRadius: "4px",
                                    },
                                    "& .css-cc8tf1": {
                                        fontWeight: Constants.defaultFontWeight,
                                    },
                                    "& .css-znmrd6-MuiButtonBase-root-MuiButton-root": {
                                        color: "#1976d2",
                                        padding: "5px 5px",
                                        borderRadius: "4px",
                                    },
                                    "& .blueCell": {
                                        color: "#080808",
                                        backgroundColor: "#d4eff8",
                                    },
                                    height: "50vh",
                                }}
                            >
                                <DataGridPro
                                    disableRowSelectionOnClick={true}
                                    sx={{
                                        '& .MuiDataGrid-columnHeader': {
                                            color: '#fff',
                                            backgroundColor: 'grey',
                                        },
                                        "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                            py: "8px",
                                        },
                                        "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                            py: "15px",
                                        },
                                        "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                            py: "22px",
                                        },
                                        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                                            width: "0.4em",
                                            height: "0.7em",
                                        },
                                        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
                                            background: "#f1f1f1",
                                        },
                                        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                                            backgroundColor: "#888",
                                        },
                                        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover": {
                                            background: "#555",
                                        },

                                        fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                    }}
                                    density="compact"
                                    slots={{ toolbar: GridToolbar }}
                                    rows={data}
                                    columns={columnNames}
                                    loading={detailLoader}
                                    getRowId={(row) => row.id}
                                    initialState={{
                                        columns: {
                                            columnVisibilityModel: {
                                                // Hide columns id, the other columns will remain visible
                                            },
                                        },
                                        sorting: {
                                            sortModel: [{ field: "updatedat", sort: "desc" }],
                                        },
                                        pagination: {
                                            paginationModel: Constants.pageSize,
                                        },
                                    }}
                                    pageSizeOptions={Constants.pageSizeOptions}
                                    pagination
                                    slotProps={{
                                        toolbar: {
                                            showQuickFilter: true,
                                            quickFilterProps: { debounceMs: 500 },
                                        },
                                    }}
                                />
                                <Box style={{ position: "absolute", top: 5, right: "270px" }}>
                                    {detailLoader ? (
                                        <CircularProgress size="1rem" />
                                    ) : (
                                        <Tooltip title="Reload List">
                                            <RefreshOutlinedIcon
                                                onClick={() => handleSearch(kapanId)}
                                                color="primary"
                                                fontSize="medium"
                                                sx={{ cursor: "pointer", mr: 1 }}
                                            />
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>

                        </Container>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="contained" color="error" onClick={close}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Box>
        </div>
    );
};
export default KapanHistoryModal;